import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

/** A numbered, iconified section wrapper that gives the long form a clear
 *  visual rhythm — each block reads as a distinct step rather than a wall
 *  of stacked inputs. */
export function FormSection({
  index,
  icon: Icon,
  title,
  hint,
  optional,
  trailing,
  children,
}: {
  index: number;
  icon: LucideIcon;
  title: string;
  hint?: string;
  optional?: boolean;
  trailing?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section
      className="group/section relative rounded-xl border border-base-300/50 bg-base-200/50 p-4 sm:p-5 transition-colors duration-200 hover:border-primary/30 animate-in fade-in slide-in-from-bottom-1"
      style={{
        animationDelay: `${index * 60}ms`,
        animationDuration: "320ms",
        animationFillMode: "backwards",
      }}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <h3 className="flex items-center gap-3 text-sm font-semibold text-base-content/90">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary ring-1 ring-primary/15 transition-transform duration-200 group-hover/section:scale-105">
            <Icon className="h-4 w-4" strokeWidth={2.25} />
          </span>
          <span className="text-[10px] font-mono font-semibold tracking-widest text-base-content/30">
            {String(index).padStart(2, "0")}
          </span>
          {title}
          {optional && (
            <span className="badge badge-ghost badge-xs font-normal text-base-content/40">
              optionnel
            </span>
          )}
        </h3>
        {trailing}
      </div>
      {hint && <p className="text-xs text-base-content/45 mt-0.5">{hint}</p>}
      <div className="mt-3">{children}</div>
    </section>
  );
}
